import React, { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { styled } from 'styled-components';

import { type Values } from '../../helpers/types';
import { type Theme } from '../../theme/theme';
import type { IconComponent } from '../Icon/createIcon';

/**
 * TAG CONSTANTS
 */
export const tagVariants = {
  default: 'blue',
  blue: 'blue',
  green: 'green',
  grey: 'grey',
  orange: 'orange',
  purple: 'purple',
  red: 'red',
  yellow: 'yellow',
} as const;
export const tagVariantsKeys = Object.keys(tagVariants) as TagVariant[];

/**
 * MAPPING
 */
export const textColorMapping = (theme: Theme): Record<TagVariant | 'disabled', string> => ({
  [tagVariants.blue]: theme.designTokens.tagDefaultTextColor,
  [tagVariants.green]: theme.designTokens.tagGreenTextColor,
  [tagVariants.grey]: theme.designTokens.tagGreyTextColor,
  [tagVariants.orange]: theme.designTokens.tagOrangeTextColor,
  [tagVariants.purple]: theme.designTokens.tagPurpleTextColor,
  [tagVariants.red]: theme.designTokens.tagRedTextColor,
  [tagVariants.yellow]: theme.designTokens.tagYellowTextColor,
  disabled: theme.designTokens.tagDisabledTextColor,
});

export const backgroundColorMapping = (theme: Theme): Record<TagVariant | 'removable' | 'disabled', string> => ({
  [tagVariants.blue]: theme.designTokens.tagDefaultBackgroundColor,
  [tagVariants.green]: theme.designTokens.tagGreenBackgroundColor,
  [tagVariants.grey]: theme.designTokens.tagGreyBackgroundColor,
  [tagVariants.orange]: theme.designTokens.tagOrangeBackgroundColor,
  [tagVariants.purple]: theme.designTokens.tagPurpleBackgroundColor,
  [tagVariants.red]: theme.designTokens.tagRedBackgroundColor,
  [tagVariants.yellow]: theme.designTokens.tagYellowBackgroundColor,
  removable: theme.designTokens.tagRemovableBackgroundColor,
  disabled: theme.designTokens.tagDisabledBackgroundColor,
});

/**
 * TAG TYPES
 */
export type TagVariant = Values<typeof tagVariants>;
type TagProps = {
  disabled?: boolean;
  icon?: IconComponent;
  label?: string;
  variant?: TagVariant;
};
type DivProps = Omit<ComponentPropsWithoutRef<'div'>, keyof TagProps>;
export type Props = DivProps & TagProps;

/**
 * CSS FUNCTIONS
 */
export const getTextColor = ({ theme, disabled, variant = tagVariants.default }: Props & { theme: Theme }): string =>
  disabled ? textColorMapping(theme).disabled : textColorMapping(theme)[variant];

export const getBackgroundColor = ({
  theme,
  disabled,
  variant = tagVariants.default,
}: Props & { theme: Theme }): string =>
  disabled ? backgroundColorMapping(theme).disabled : backgroundColorMapping(theme)[variant];

/**
 * Stylized Icons for Tag
 */
const IconWrapper = styled.span`
  font-size: 12px;
  padding: 2px;
  display: inline-flex;
`;

/**
 * Tag Component
 */
export const Tag = forwardRef<HTMLDivElement, Props>(
  ({ disabled = false, variant = tagVariants.default, icon: IconEl, label, ...props }, ref) => (
    <TagStyled ref={ref} disabled={disabled} variant={variant} {...props}>
      {label && <TagContentStyled>{label}</TagContentStyled>}
      {IconEl && (
        <IconWrapper>
          <IconEl />
        </IconWrapper>
      )}
    </TagStyled>
  ),
);

Tag.displayName = 'Tag';
export const TagContentStyled = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const TagStyled = styled.div<Props>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  position: relative;

  min-height: 24px;
  min-width: 32px;
  max-width: 100%;
  margin: 0;
  padding: 0 0.25rem;

  background-color: ${getBackgroundColor};

  border: none;
  border-radius: ${({ theme }) => theme.designTokens.tagRadius};

  color: ${getTextColor};
  font: inherit;
  font-family: ${({ theme }) => theme.designTokens.tagFontFamily};
  font-size: ${({ theme }) => theme.designTokens.tagFontSize};
  font-weight: ${({ theme }) => theme.designTokens.tagFontWeight};
  line-height: ${({ theme }) => theme.designTokens.tagFontLineHeight};
  text-align: center;
`;
